import { Routes } from '@angular/router';
import { environment } from '../environments/environment';
import { authGuard } from './core/guards/auth.guard';
import { unsavedChangesGuard } from './core/guards/unsaved-changes.guard';
import { PAGE_IDS } from './core/router/page-ids';
import { PageHeaderData } from './core/router/router-state-serializer';
import { PATIENT_FEATURE_PROVIDERS } from './patient/patient.providers';
import { PROXY_ACCESS_PROVIDERS } from './proxy-access/proxy-access.providers';

export const routes: Routes = [
  {
    path: '',
    loadComponent: () => import('./login/login.component').then(m => m.LoginComponent),
    data: { id: PAGE_IDS.LOGIN, title: `Login | ${environment.title}`, pageHeaderData: null, pageFooterData: null },
  },
  {
    path: 'error',
    loadComponent: () => import('./error-page/components/error-page.component').then(m => m.ErrorPageComponent),
    data: { id: PAGE_IDS.ERROR, title: `Error | ${environment.title}`, pageHeaderData: null, pageFooterData: null },
  },
  {
    path: 'privacy-policy',
    loadComponent: () => import('./privacy-policy/privacy-policy.component').then(m => m.PrivacyPolicyComponent),
    data: {
      id: PAGE_IDS.PRIVACY,
      title: `Privacy Policy | ${environment.title}`,
      pageHeaderData: new PageHeaderData('Privacy Policy'),
      pageFooterData: null,
    },
  },
  {
    path: 'terms-of-use',
    loadComponent: () => import('./terms-of-use/terms-of-use.component').then(m => m.TermsOfUseComponent),
    data: {
      id: PAGE_IDS.TERMS,
      title: `Terms of Use | ${environment.title}`,
      pageHeaderData: new PageHeaderData('Terms of Use'),
      pageFooterData: null,
    },
  },
  {
    path: 'unauthorized',
    loadComponent: () =>
      import('./auth-workflow/components/unauthorized/unauthorized.component').then(m => m.UnauthorizedComponent),
    data: {
      id: PAGE_IDS.UNAUTHORIZED,
      title: `Unauthorized | ${environment.title}`,
      pageHeaderData: null,
      pageFooterData: null,
    },
  },
  {
    path: 'invalid-patient',
    loadComponent: () => import('./invalid-patient/invalid-patient.component').then(m => m.InvalidPatientComponent),
    data: {
      id: PAGE_IDS.INVALID_PATIENT,
      title: `Unauthorized | ${environment.title}`,
      pageHeaderData: null,
      pageFooterData: null,
    },
  },
  {
    path: 'assign-patient',
    loadComponent: () =>
      import('./user/components/patient-assignment/patient-assignment.component').then(
        m => m.PatientAssignmentComponent
      ),
    data: {
      id: PAGE_IDS.ASSIGN_PATIENT,
      title: `Assign Patient | ${environment.title}`,
      pageHeaderData: null,
      pageFooterData: null,
    },
  },
  {
    // Component-less, empty-path parent that owns the proxy-access store for the whole public
    // caregiver flow — the arrangement NgRx documents for sibling routes that share a feature.
    //
    // Root routes inherit none of PATIENT_FEATURE_PROVIDERS, so the slice and effects have to be
    // provided out here or the form dispatches into the void and its selectors read a missing key.
    // They belong on the parent rather than on each screen because the two screens hand state to each
    // other: the form submits, and the confirmation reads the accepted request's reference number back
    // out of the slice. One injector that outlives both navigations is the arrangement that obviously
    // holds that, instead of one that happens to hold it — `provideState` today only ever *adds* a
    // feature (`featureStateProviderFactory`; the `removeFeatures` teardown belongs to the NgModule
    // `StoreModule.forFeature` path this repo doesn't use), so per-route registration also survives,
    // but only by relying on NgRx never reclaiming it. See the public-flow integration spec.
    //
    // Empty path, so the URLs stay `/proxy-access-request` and `/proxy-access-confirmation`. It is
    // declared after the login route, which claims the bare `''` URL with a full match.
    path: '',
    providers: [...PROXY_ACCESS_PROVIDERS],
    children: [
      {
        // Caregiver-initiated proxy access, reached from "No, I am a caregiver or guardian" on
        // assign-patient. Public on purpose: the user is still Pending with no patientId, so the
        // authGuard-protected account route (patient/:patientId/proxy-access-form) would bounce them
        // straight back here. `isCaregiverRequest` is what tells the shared form to collect the
        // patient's details instead of prefilling them from the record on file.
        path: 'proxy-access-request',
        loadComponent: () =>
          import('./proxy-access/components/proxy-access-form/proxy-access-form.component').then(
            m => m.ProxyAccessFormComponent
          ),
        data: {
          id: PAGE_IDS.PROXY_ACCESS_REQUEST,
          title: `Request Proxy Access | ${environment.title}`,
          pageHeaderData: null,
          pageFooterData: null,
          isCaregiverRequest: true,
        },
        // Browser Back / gesture, not just the in-page back control: the component owns the prompt
        // so a signed-but-unsubmitted authorization can't be discarded silently.
        canDeactivate: [unsavedChangesGuard],
      },
      {
        // Where the caregiver form lands after a valid submit. Public for the same reason as the form.
        path: 'proxy-access-confirmation',
        loadComponent: () =>
          import('./proxy-access/components/proxy-access-confirmation/proxy-access-confirmation.component').then(
            m => m.ProxyAccessConfirmationComponent
          ),
        data: {
          id: PAGE_IDS.PROXY_ACCESS_CONFIRMATION,
          title: `Authorization Submitted | ${environment.title}`,
          pageHeaderData: null,
          pageFooterData: null,
          isCaregiverRequest: true,
        },
      },
    ],
  },
  {
    path: 'workflow-holding',
    loadComponent: () =>
      import('./auth-workflow/components/workflow-holding/workflow-holding.component').then(
        m => m.WorkflowHoldingComponent
      ),
    data: {
      id: PAGE_IDS.WORKFLOW_HOLDING,
      title: `Account In Progress | ${environment.title}`,
      pageHeaderData: null,
      pageFooterData: null,
    },
  },
  {
    path: 'manual-review',
    loadComponent: () =>
      import('./auth-workflow/components/manual-review/manual-review.component').then(m => m.ManualReviewComponent),
    data: {
      id: PAGE_IDS.MANUAL_REVIEW,
      title: `Account Under Review | ${environment.title}`,
      pageHeaderData: null,
      pageFooterData: null,
    },
  },
  {
    path: 'patient/:patientId',
    canActivate: [authGuard],
    providers: [...PATIENT_FEATURE_PROVIDERS],
    loadChildren: () => import('./patient/patient.routes').then(m => m.PATIENT_ROUTES),
  },
  {
    path: '**',
    loadComponent: () => import('./page-not-found/page-not-found.component').then(m => m.PageNotFoundComponent),
    data: {
      id: PAGE_IDS.NOT_FOUND,
      title: `Not Found | ${environment.title}`,
      pageHeaderData: null,
      pageFooterData: null,
    },
  },
];
